import { React, useState, useEffect } from "react"
import {
    Grid,
    Button,
    Typography,
    TextField,
    useTheme,
    useMediaQuery
} from "@mui/material"
import { useSelector, useDispatch } from "react-redux"
import HighlightOffIcon from "@mui/icons-material/HighlightOff"
import { Autocomplete, TextField as TF, IconButton } from "@mui/material"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import dayjs, { Dayjs } from "dayjs"
import { LocalizationProvider, DateTimePicker } from "@mui/x-date-pickers"
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined"
import InsertInvitationIcon from "@mui/icons-material/InsertInvitation"

import useStyles, { AddConfButtonSmallStyles, AddHostButtonStyles, CalendarIconColorStyles, ConfRoomEntryStyles, floatingLabelFocusStyleStyles, HostInnerStyles, notchedOutlineStyles, NoVisitorsStyles, TextColorStyles, VisitorBoxInputsStyles } from "./styles/VisitorStyles"
import { updateConfRoomList } from "../../actions/visitorFormActions"
import Alert from "../main/Alert"

/* Component that defines the form area to input conference room details relating to the
    specific visit. */
function ConferenceRoomBox({ visitStart, visitEnd, mode }) {
    const dispatch = useDispatch()
    const theme = useTheme()

    const visitStartDateTime = useSelector(
        (state) => state.visitFeed.visitStartDateTime
    )
    const visitEndDateTime = useSelector(
        (state) => state.visitFeed.visitEndDateTime
    )

    // constants for conditional styling adjustments due to screen size (px)
    const isMediumScreen = useMediaQuery(theme.breakpoints.down("1040"))
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("800"))
    const isSmallPhone = useMediaQuery(theme.breakpoints.down("400"))

    // list of all active conference rooms loaded from database (store)
    const dbConfRooms = useSelector((state) => state.visitor.activeConfRooms)

    // state representation of conference room information entry
    const [confRoomInfo, setConfRoomInfo] = useState({
        name: "",
        fromDateTime: null,
        toDateTime: null,
        id: ""
    })

    /* EDIT mode: get original conf room list and manipulate data to match state
        defined variables */
    const currentConfRoomList = useSelector(
        (state) => state.visitFeed.currentVisitConfRooms
    )
    let formatConfRoomList
    if (mode === "edit") {
        if (currentConfRoomList?.length > 0) {
            formatConfRoomList = currentConfRoomList.map((room) => {
                const start = new Date(room.StartDateTime)
                const end = new Date(room.EndDateTime)
                const id = room.ConferenceRoomId
                const roomObj = dbConfRooms.find(
                    (conf) => conf.ConferenceRoomId === id
                )

                return {
                    name: roomObj?.ConferenceRoomName,
                    //  +
                    // " [" +
                    // room.ConferenceRoomLocation +
                    // "]"
                    fromDateTime: start,
                    toDateTime: end,
                    id: id
                }
            })
        } else {
            formatConfRoomList = []
        }
    }

    // state representation of (dynamic) list of conference room entries from form entry
    const [confRoomList, setConfRoomList] = useState(
        mode === "create" ? [] : formatConfRoomList
    )

    // list of all conference room entries from store
    const allConfRooms = useSelector((state) => state.visitor.allConfRooms)

    // error alert message
    const [alertFlag, setAlertFlag] = useState(false)
    const [alertText, setAlertText] = useState("")

    // array of active conference rooms formatted for Autocomplete dropdown ({id: ..., name: ...})
    const conferenceRoomDropDown = dbConfRooms.map((room) => {
        return {
            id: room.ConferenceRoomId,
            name:
                room.ConferenceRoomName +
                " [" +
                room.ConferenceRoomLocation +
                "]"
        }
    })
    conferenceRoomDropDown.splice(0, 0, { id: "", name: "" })

    /* Handler to clear state representation of conference room input boxes */
    const clearConfRoomInput = () => {
        setConfRoomInfo({
            name: "",
            fromDateTime: null,
            toDateTime: null,
            id: ""
        })
    }

    /* Handler for "ADD" button to add conference room entry to state representation of list,
        then updates conference room entries list in store */
    const handleClickAdd = async () => {
        // error checks relies on visit start and end times
        console.log("visitStart: ", visitStart)
        console.log("visitEnd: ", visitEnd)

        if (
            !visitStart ||
            !visitEnd ||
            isNaN(visitStart.getTime()) ||
            isNaN(visitEnd.getTime())
        ) {
            setAlertFlag(true)
            setAlertText(
                "Please first provide valid visit start and end date/time."
            )
            return
        }
        // check for valid date object
        if (
            !confRoomInfo.fromDateTime ||
            !confRoomInfo.toDateTime ||
            isNaN(confRoomInfo.fromDateTime.getTime()) ||
            isNaN(confRoomInfo.toDateTime.getTime())
        ) {
            setAlertFlag(true)
            setAlertText("Please enter a valid date and time.")
            return
        } // check for valid date inputs
        else if (confRoomInfo.fromDateTime >= confRoomInfo.toDateTime) {
            setAlertFlag(true)
            setAlertText("Please provide a valid date and time interval.")
            return
        } // time range must be between visit date and time range
        else if (
            !(
                visitStart <= confRoomInfo.fromDateTime &&
                confRoomInfo.fromDateTime <= visitEnd &&
                visitStart <= confRoomInfo.toDateTime &&
                confRoomInfo.toDateTime <= visitEnd
            )
        ) {
            setAlertFlag(true)
            setAlertText(
                "Please provide a date and time interval within the visit range."
            )
            return
        }
        // check for duplicates
        else if (
            filteredConfRoom(confRoomInfo).length !== confRoomList.length
        ) {
            setAlertFlag(true)
            setAlertText(
                "It looks like this conference room entry already exists."
            )
            return
        } // check for blank inputs
        else if (confRoomInfo.name !== "") {
            confRoomList.push(confRoomInfo)
            setConfRoomList(confRoomList)
            await dispatch(updateConfRoomList(confRoomList))
            setAlertFlag(false)
        }
        // clear input boxes
        clearConfRoomInput()
    }

    /* Function that filters an array of conference-room-detail objects, returning
        final array that does not include input parameter object (match data) */
    const filteredConfRoom = (roomObj) => {
        const filteredConf = confRoomList.filter(
            (elem) =>
                roomObj.id !== elem.id ||
                roomObj.fromDateTime.toLocaleString([], {
                    dateStyle: "medium",
                    timeStyle: "short"
                }) !==
                    elem.fromDateTime.toLocaleString([], {
                        dateStyle: "medium",
                        timeStyle: "short"
                    }) ||
                roomObj.toDateTime.toLocaleString([], {
                    dateStyle: "medium",
                    timeStyle: "short"
                }) !==
                    elem.toDateTime.toLocaleString([], {
                        dateStyle: "medium",
                        timeStyle: "short"
                    })
        )
        return filteredConf
    }

    /* Handler for "x" delete conference room entry button */
    const handleDelete = async (room) => {
        let removedConfRooms = filteredConfRoom(room)
        setConfRoomList(removedConfRooms)
        await dispatch(updateConfRoomList(removedConfRooms))
    }

    /* Handler for all changes in dropdown Autocomplete field */
    const onChangeConfRoom = (event, newVal, reason) => {
        if (reason === "clear") {
            clearConfRoomInput()
        } else {
            setConfRoomInfo({
                ...confRoomInfo,
                name: newVal.name,
                id: newVal.id
            })
        }
    }

    /* Handler for all changes in date-time picker fields */
    const onChangeDateInputs = (val, name) => {
        setConfRoomInfo({ ...confRoomInfo, [name]: val })
    }

    /* Handler for alert message */
    const handleAlertDialog = (event, reason) => {
        if (reason === "clickaway") {
            return
        }
        setAlertFlag(false)
        //clearConfRoomInput()
    }

    /* React hook; updates state repr. with list from store */
    useEffect(() => {
        async function runUseEffectFunctions() {
            // on component render, set state variables to saved data
            if (mode === "edit" && formatConfRoomList) {
                await dispatch(updateConfRoomList(formatConfRoomList))
            }
        }
        runUseEffectFunctions()
    }, [dispatch])

    return (
        <>
            <Grid item container sx={HostInnerStyles}>
                {/* Case on number of Conference Room info entries, to display */}
                {allConfRooms.length !== 0 ? (
                    allConfRooms?.map((room, index) => {
                        return (
                            <Grid container justify="center" key={index} size={12}>
                                <Grid
                                    item
                                    size={{xs: 3, sm: 3, md: 4}}
                                    align="center"
                                    sx={ConfRoomEntryStyles}
                                >
                                    {room.name}
                                </Grid>
                                <Grid
                                    item
                                    size={3}
                                    align="center"
                                    sx={ConfRoomEntryStyles}
                                >
                                    {isMediumScreen
                                        ? room.fromDateTime.toLocaleString([], {
                                              dateStyle: "short",
                                              timeStyle: "short"
                                          })
                                        : room.fromDateTime.toLocaleString([], {
                                              dateStyle: "medium",
                                              timeStyle: "short"
                                          })}
                                </Grid>
                                <Grid
                                    item
                                    size={3}
                                    align="center"
                                    sx={ConfRoomEntryStyles}
                                >
                                    {isMediumScreen
                                        ? room.toDateTime.toLocaleString([], {
                                              dateStyle: "short",
                                              timeStyle: "short"
                                          })
                                        : room.toDateTime.toLocaleString([], {
                                              dateStyle: "medium",
                                              timeStyle: "short"
                                          })}
                                </Grid>
                                <Grid item size={2} align="center">
                                    <IconButton
                                        color="inherit"
                                        onClick={(e) => handleDelete(room)}
                                    >
                                        <HighlightOffIcon fontSize="small" />
                                    </IconButton>
                                </Grid>
                            </Grid>
                        )
                    })
                ) : (
                    <Grid item size={12}>
                        <Typography
                            align="center"
                            sx={NoVisitorsStyles}
                        >
                            No conference rooms added.
                        </Typography>
                    </Grid>
                )}

                {/* Alerts pop-up area */}
                <Grid
                    item
                    container
                    size={12}
                    alignItems="center"
                    justify="flex-end"
                >
                    <Grid item>
                        {alertFlag && (
                            <Alert onClose={handleAlertDialog} severity="error">
                                {alertText}
                            </Alert>
                        )}
                    </Grid>
                </Grid>

                {/* Conference room selector dropdown component */}
                <Grid
                    item
                    size={{xs: 3, sm: 3, md: 4}}
                    sx={VisitorBoxInputsStyles}
                >
                    <Autocomplete
                        disablePortal
                        size="small"
                        options={conferenceRoomDropDown}
                        getOptionLabel={(roomObject) => roomObject.name}
                        value={confRoomInfo}
                        isOptionEqualToValue={(option, value) =>
                            option.id === value.id
                        }
                        onChange={onChangeConfRoom}
                        renderInput={(params) => (
                            <TF
                                {...params}
                                label="Name"
                                variant="outlined"
                                InputProps={{
                                    ...params.InputProps,
                                    classes: {
                                        notchedOutline: notchedOutlineStyles
                                    },
                                    sx: TextColorStyles
                                }}
                                InputLabelProps={{
                                    ...params.InputLabelProps,
                                    sx: floatingLabelFocusStyleStyles
                                }}
                            />
                        )}
                    />
                </Grid>

                {/* Conference Room Date/Time (Begin) input selector component */}
                <Grid
                    item
                    size={3}
                    sx={VisitorBoxInputsStyles}
                >
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DateTimePicker
                            value={dayjs(confRoomInfo.fromDateTime)}
                            label="FROM"
                            minDate={visitStart ? dayjs(visitStart) : null}
                            maxDate={visitEnd ? dayjs(visitEnd) : null}
                            onChange={(val) =>
                                onChangeDateInputs(val?.$d, "fromDateTime")
                            }
                            slots={{
                                openPickerIcon: InsertInvitationIcon
                            }}
                            slotProps={{
                                textField: {
                                    variant: "standard",
                                    error: false,
                                    sx: CalendarIconColorStyles
                                },
                                openPickerIcon: {
                                    sx: TextColorStyles
                                }
                            }}
                        />
                    </LocalizationProvider>
                </Grid>

                {/* Conference Room Date/Time (End) input selector component */}
                <Grid
                    item
                    size={3}
                    sx={VisitorBoxInputsStyles}
                >
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DateTimePicker
                            value={dayjs(confRoomInfo.toDateTime)}
                            label="TO"
                            minDate={visitStart ? dayjs(visitStart) : null}
                            maxDate={visitEnd ? dayjs(visitEnd) : null}
                            onChange={(val) =>
                                onChangeDateInputs(val?.$d, "toDateTime")
                            }
                            slots={{
                                openPickerIcon: InsertInvitationIcon
                            }}
                            slotProps={{
                                textField: {
                                    variant: "standard",
                                    error: false,
                                    sx: CalendarIconColorStyles
                                },
                                openPickerIcon: {
                                    sx: TextColorStyles
                                }
                            }}
                        />
                    </LocalizationProvider>
                </Grid>

                {/* "ADD" button to add entered Conference Room info entry to list in area */}
                <Grid
                    item
                    size={{xs: 2, sm: 2, md: 1}}
                    align="center"
                    sx={
                        isSmallScreen
                            ? AddConfButtonSmallStyles
                            : AddHostButtonStyles
                    }
                >
                    {isSmallPhone ? (
                        <IconButton color="warning" onClick={handleClickAdd}>
                            <AddBoxOutlinedIcon fontSize="medium" />
                        </IconButton>
                    ) : (
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={handleClickAdd}
                        >
                            ADD
                        </Button>
                    )}
                </Grid>
            </Grid>
        </>
    )
}

export default ConferenceRoomBox
